import { useState } from 'react';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const MEMBERSHIP_PRODUCT_ID = '51e560f3-32dc-4d17-a751-3d228842a02a';

const benefits = [
  { icon: 'ri-lock-2-line', title: 'Exclusive Secrets', desc: 'New hidden secrets delivered every week, before anyone else' },
  { icon: 'ri-video-line', title: 'Private Videos', desc: 'Members-only video content with detailed walkthroughs' },
  { icon: 'ri-flashlight-line', title: 'Early Access', desc: 'Be first to discover new secrets before public release' },
  { icon: 'ri-magic-line', title: 'Special Tricks', desc: 'Advanced glitches and tricks only members can access' },
];

export default function MembershipSection() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleBecomeMember = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: MEMBERSHIP_PRODUCT_ID,
          successUrl: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/#membership`,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        setError('Could not start checkout. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="membership" className="relative py-16 md:py-24 overflow-hidden bg-[#0d0d18]">
      {/* Background aurora */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(ellipse, #7c3aed 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
        <div
          className="absolute bottom-0 right-0 w-[400px] h-[300px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(ellipse, #facc15 0%, transparent 70%)', filter: 'blur(80px)' }}
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 text-center">
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5"
          style={{ background: 'rgba(124,58,237,0.3)', border: '1px solid rgba(168,85,247,0.5)' }}
        >
          <i className="ri-vip-crown-line text-yellow-400 text-sm"></i>
          <span className="text-yellow-400 text-xs font-black uppercase tracking-widest">Exclusive Access</span>
        </div>

        {/* Title */}
        <h2
          className="font-black text-white text-3xl md:text-5xl mb-4"
          style={{ textShadow: '0 0 40px rgba(168,85,247,0.8)', fontFamily: "'Orbitron', sans-serif" }}
        >
          Join the Secret Club 🔐
        </h2>
        <p className="text-gray-400 text-base md:text-lg mb-12 max-w-xl mx-auto">
          Get unlimited access to all secrets, videos, and exclusive content every single week.
        </p>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-12">
          {benefits.map((b) => (
            <div
              key={b.title}
              className="flex items-start gap-4 p-5 rounded-2xl border border-purple-800/40 text-left transition-all duration-300 hover:border-purple-500/60 hover:scale-[1.02]"
              style={{ background: 'rgba(168,85,247,0.07)' }}
            >
              <div
                className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0"
                style={{ background: 'rgba(250,204,21,0.12)', border: '1px solid rgba(250,204,21,0.3)' }}
              >
                <i className={`${b.icon} text-yellow-400 text-2xl`} style={{ filter: 'drop-shadow(0 0 6px rgba(250,204,21,0.8))' }}></i>
              </div>
              <div>
                <h3 className="text-white font-black text-base mb-1">{b.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <div className="mb-8">
          <div className="flex items-baseline justify-center gap-1 mb-2">
            <span className="text-yellow-400 text-2xl font-bold">$</span>
            <span
              className="text-yellow-400 font-black"
              style={{ fontSize: 'clamp(3rem, 10vw, 5rem)', textShadow: '0 0 30px rgba(250,204,21,0.7)', fontFamily: "'Orbitron', sans-serif" }}
            >
              9
            </span>
            <span className="text-gray-400 text-xl font-semibold">/month</span>
          </div>
          <p className="text-gray-500 text-sm">Cancel anytime. No hidden fees.</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 text-red-400 text-sm bg-red-900/20 border border-red-500/30 rounded-xl py-3 px-4">
            {error}
          </div>
        )}

        {/* CTA */}
        <button
          onClick={handleBecomeMember}
          disabled={loading}
          className="inline-flex items-center gap-3 bg-yellow-400 text-black font-black uppercase tracking-wider px-10 py-4 rounded-full whitespace-nowrap transition-all duration-200 hover:bg-yellow-300 hover:scale-105 text-base md:text-lg disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          style={{ boxShadow: '0 0 40px rgba(250,204,21,0.6), 0 0 80px rgba(250,204,21,0.2)' }}
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-xl"></i>
              Loading...
            </>
          ) : (
            <>
              <i className="ri-vip-crown-line text-xl"></i>
              Become a Member
            </>
          )}
        </button>

        {/* Trust row */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 md:gap-8">
          {[
            { icon: 'ri-shield-check-line', text: 'Secure Payment' },
            { icon: 'ri-flashlight-line', text: 'Instant Access' },
            { icon: 'ri-refresh-line', text: 'Cancel Anytime' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-1.5 text-gray-500 text-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${item.icon} text-yellow-600 text-sm`}></i>
              </div>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
